/* eslint-disable react/no-unescaped-entities */
import PillarPage from "./PillarPage";

const HERO = "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=1800&q=85&auto=format&fit=crop";

const TOC = [
  { id: "overview", label: "Can foreigners buy Dubai property?" },
  { id: "freehold-zones", label: "Where non-residents can buy" },
  { id: "remote-process", label: "The remote purchase, step by step" },
  { id: "documents", label: "Documents you'll need from abroad" },
  { id: "payment-transfer", label: "Moving money into the UAE" },
  { id: "mortgage-non-resident", label: "Financing from outside the UAE" },
  { id: "poa-and-visits", label: "Power of attorney & site visits" },
  { id: "residency-outcome", label: "Residency & Golden Visa post-purchase" },
  { id: "pitfalls", label: "Common mistakes remote buyers make" },
];

const SECTIONS = [
  {
    id: "overview",
    heading: "Buying Dubai property from abroad — the 2026 reality",
    body: (
      <>
        <p>
          Roughly <b>42% of Point Penta transactions in 2025 closed with the buyer never physically
          in Dubai</b>. UAE law explicitly permits foreign nationals to purchase freehold property
          in designated zones without residency, a visa, an Emirates ID or a UAE bank account at
          the point of offer. What you do need is a licensed brokerage that can execute the
          purchase, escrow, transfer and post-completion admin remotely — which is a specialism, not
          a courtesy.
        </p>
        <p>
          This guide walks through the exact steps used by our international desk to close a Dubai
          apartment or villa from London, Riyadh, Mumbai, Hong Kong or New York — including the
          documents, wire routes, power-of-attorney templates and the two DLD counters that require
          physical presence (both of which we cover on your behalf).
        </p>
      </>
    ),
  },
  {
    id: "freehold-zones",
    heading: "Where non-residents can buy freehold in Dubai",
    body: (
      <>
        <p>
          Foreign ownership in Dubai is restricted to <b>designated freehold zones</b>, defined by
          the Dubai Government under Regulation No. 3 of 2006. Every Point Penta mandate sits inside
          a freehold zone by design — if you have seen an address on our site, non-residents can own
          it outright. The most active zones for international buyers are:
        </p>
        <ul>
          <li><b>Downtown Dubai</b> — Burj Khalifa district, Opera views, prestige apartments.</li>
          <li><b>Palm Jumeirah</b> — signature villas, hotel-branded residences (Atlantis, One&amp;Only, Six Senses).</li>
          <li><b>Dubai Marina & JBR</b> — waterfront towers with the highest yields in prime.</li>
          <li><b>Business Bay</b> — canal-side apartments, strong short-let dynamics.</li>
          <li><b>Emirates Hills, Dubai Hills, Al Barari</b> — freehold villas for family relocation.</li>
          <li><b>JVC, JLT, Arjan</b> — mid-market yield plays (6.8–7.8% gross).</li>
        </ul>
        <p>
          Non-freehold zones (Deira, Bur Dubai, older Al Karama) are technically off-limits to
          non-GCC nationals as full ownership; leasehold up to 99 years is available in a small
          subset of these but is rarely relevant to an international investment case.
        </p>
      </>
    ),
  },
  {
    id: "remote-process",
    heading: "The remote purchase — 10 steps from brief to keys",
    body: (
      <ol>
        <li><b>Discovery call</b> — 45–60 min with a Point Penta partner to agree budget, community shortlist, holding horizon (own-use vs yield), and financing route.</li>
        <li><b>Curated shortlist</b> — 3–6 mandates delivered within 48 hours, with our own valuation floor and yield modelling on each.</li>
        <li><b>Virtual viewing</b> — live walkthrough on WhatsApp / Zoom with your assigned advisor, floor plans and building spec shared over email.</li>
        <li><b>Offer & MoU (Form F)</b> — signed digitally via DocuSign, 10% deposit released to escrow (never to the seller directly).</li>
        <li><b>Developer / seller sign-off</b> — signature counterparts couriered via DHL or executed via a UAE-based Power of Attorney (see section 7).</li>
        <li><b>NOC application</b> — Point Penta secures the developer No-Objection Certificate; typically 5–10 business days, no buyer action needed.</li>
        <li><b>Wire transfer to escrow / DLD</b> — full balance transferred following the SWIFT instructions issued after MoU. UAE receiving bank confirms same-day.</li>
        <li><b>Transfer at Dubai Land Department</b> — attended by Point Penta on your behalf under a notarised PoA. Title deed issued digitally within 4 hours.</li>
        <li><b>Ejari & handover</b> — utility registration (DEWA), Ejari lease registration if the property is to be rented, snagging report if new.</li>
        <li><b>Golden Visa filing</b> — if the purchase clears AED 2M, we submit the visa application in parallel; residency is issued in 4–8 weeks.</li>
      </ol>
    ),
  },
  {
    id: "documents",
    heading: "Documents you'll need from abroad",
    body: (
      <>
        <p>
          The document list is short but each item must be executed correctly the first time. Here
          is the exact checklist we send every remote buyer at kickoff:
        </p>
        <ul>
          <li><b>Passport copy</b> — coloured scan, all pages, 6+ months validity.</li>
          <li><b>Second ID</b> — driving licence or national ID (helps banks with KYC).</li>
          <li><b>Proof of address</b> — utility bill or bank statement issued within the last 3 months.</li>
          <li><b>Source-of-funds letter</b> — one-page letter from your bank confirming the origin of the purchase capital (salary, business sale, portfolio liquidation, inheritance). Required by UAE Central Bank AML rules on wires over USD 100k.</li>
          <li><b>Power of Attorney</b> — if you cannot attend DLD transfer in person. Notarised in your home country and legalised at the UAE embassy (or apostilled if the country is a Hague Convention signatory — most Point Penta clients are).</li>
          <li><b>Recent bank statement</b> — 3 most recent months, showing the funds are yours.</li>
          <li><b>Marital status / spouse consent</b> — only if buying in joint names or under community-property jurisdictions.</li>
        </ul>
        <p>
          None of the above need to be notarised in the UAE — they must be notarised where you are.
          Point Penta liaises with the notary and provides English/Arabic bilingual templates so
          your local lawyer does not have to interpret UAE-specific language.
        </p>
      </>
    ),
  },
  {
    id: "payment-transfer",
    heading: "Moving money into the UAE — SWIFT, exchange houses and safety rails",
    body: (
      <>
        <h3>Preferred routes</h3>
        <p>
          For purchases <b>above AED 500,000</b> we recommend direct bank-to-bank SWIFT in USD, GBP,
          EUR or AED, routed to the developer's project escrow account (off-plan) or the seller's
          UAE bank account (ready property) via DLD. Escrow accounts held with Emirates NBD, Mashreq,
          Dubai Islamic Bank and ADCB are the four most commonly used and are all Central-Bank
          supervised — the fastest and cleanest AML profile.
        </p>
        <h3>What we actively avoid</h3>
        <ul>
          <li><b>Third-party transfers</b> — money must move from an account in your name to a UAE receiving account; funds arriving from a third party will be flagged and can delay closing by 2–4 weeks.</li>
          <li><b>Cash</b> — DLD accepts wire and manager's cheque only.</li>
          <li><b>Crypto direct-to-developer</b> — a handful of Dubai developers accept USDT / BTC via licensed exchanges (e.g. Rain, BitOasis), but stablecoin-first purchases still require an AED-denominated Form F. If crypto is your source of funds, off-ramp locally and wire in fiat.</li>
        </ul>
      </>
    ),
  },
  {
    id: "mortgage-non-resident",
    heading: "Financing from outside the UAE",
    body: (
      <>
        <p>
          Non-resident mortgages are available and Point Penta's mortgage desk has active
          relationships with the ten most active lenders. Key parameters for 2026:
        </p>
        <ul>
          <li><b>Loan-to-value</b> — 50–60% for non-residents (vs 80% for residents); UAE Central Bank rule.</li>
          <li><b>Minimum property value</b> — most banks lend from AED 1M; some (HSBC, Standard Chartered) prefer AED 3M+.</li>
          <li><b>Rate range 2026</b> — 4.75% – 6.50% (variable), 5.00% – 6.75% (fixed 1-5 yrs).</li>
          <li><b>Tenor</b> — up to 25 years, capped at age 65–70 at maturity.</li>
          <li><b>Documents</b> — 6 months salary slips / accountant-signed P&amp;L for self-employed, 12 months bank statements, existing liabilities disclosure.</li>
          <li><b>Time to pre-approval</b> — 3–5 working days for salaried, 7–14 days for self-employed / business owners.</li>
        </ul>
        <p>
          If a mortgage is on the table, always secure pre-approval <em>before</em> negotiating the
          MoU — the deposit is at risk if financing falls through. Point Penta arranges pre-approval
          from your home country entirely by email.
        </p>
      </>
    ),
  },
  {
    id: "poa-and-visits",
    heading: "Power of Attorney — the remote closer's superpower",
    body: (
      <>
        <p>
          A UAE-compatible Power of Attorney (PoA) lets a Point Penta partner sign every document
          on your behalf: MoU, developer forms, NOC applications, DLD transfer, Ejari, DEWA account
          opening, even Golden Visa medical scheduling. It is the single fastest way to close a Dubai
          purchase without flying in.
        </p>
        <h3>How to execute a PoA remotely</h3>
        <ol>
          <li>Point Penta drafts a bilingual (English + Arabic) PoA tailored to the transaction.</li>
          <li>You sign in front of a notary in your home country.</li>
          <li>The notarised PoA is <b>apostilled</b> (if your country is a Hague signatory) or <b>legalised at the UAE embassy</b> (if not).</li>
          <li>Original is couriered to Dubai. It is then attested by the UAE Ministry of Foreign Affairs and legally translated by a sworn Arabic translator.</li>
          <li>Total elapsed time: 7–15 business days end-to-end from most G20 countries.</li>
        </ol>
        <h3>If you do want to fly in</h3>
        <p>
          A single 2-day trip is enough to sign at DLD in person. Point Penta arranges the DLD
          appointment, chauffeur transport, notary and post-completion site inspection to fit the
          window. Most clients choose to visit once — for handover, not signature.
        </p>
      </>
    ),
  },
  {
    id: "residency-outcome",
    heading: "Residency & Golden Visa after buying from abroad",
    body: (
      <>
        <p>
          The moment the title deed is issued in your name (or Oqood for off-plan) you may apply for
          UAE residency without moving:
        </p>
        <ul>
          <li><b>AED 750K+ purchase</b> → 2-year investor visa. Family sponsorship available.</li>
          <li><b>AED 2M+ purchase</b> → 10-year Golden Visa. Zero minimum stay requirement — you can keep it active while living abroad indefinitely.</li>
          <li><b>Off-plan qualifies too</b> — Oqood + 50%+ paid unlocks either visa (2022 rule change).</li>
        </ul>
        <p>
          The medical fitness test and biometric capture must happen in the UAE. Most non-residents
          complete both on a single 48-hour trip after the property closes; Point Penta books
          concierge Amer Center appointments so the visa is stamped before you fly out.
        </p>
      </>
    ),
  },
  {
    id: "pitfalls",
    heading: "The five mistakes remote buyers pay us to prevent",
    body: (
      <ul>
        <li><b>Wiring to the seller's personal account instead of escrow.</b> Never do this. Off-plan money must sit in the RERA-supervised project escrow; ready-property money moves through DLD counters. Escrow protects you if the seller defaults.</li>
        <li><b>Signing the MoU before securing mortgage pre-approval.</b> If financing is refused, the deposit is at risk. Get pre-approval first.</li>
        <li><b>Buying in joint names without a clear survivorship structure.</b> UAE inheritance follows Sharia by default; a DIFC will or the pending federal non-Muslim inheritance framework should be executed at time of purchase, not after.</li>
        <li><b>Under-budgeting closing costs.</b> Add ~7% to the sticker price: 4% DLD transfer, 2% agency, ~1% miscellaneous (NOC, Oqood, trustee).</li>
        <li><b>Ignoring service charges.</b> They vary from AED 12/sqft (JVC) to AED 45/sqft (Palm Jumeirah). This can be 3–4% of the annual gross rent — always model it into the yield.</li>
      </ul>
    ),
  },
];

const FAQS = [
  { q: "Can I buy Dubai property from abroad without visiting?", a: "Yes. Point Penta closes roughly 42% of transactions with the buyer never in Dubai — via a notarised Power of Attorney signed in your home country. The only steps needing physical UAE presence are the medical fitness test and biometric capture if you claim the resulting investor or Golden Visa, which is optional." },
  { q: "Do I need a UAE bank account to buy a Dubai property?", a: "No. Non-residents wire directly from their home-country bank in USD, GBP, EUR or AED to the developer's project escrow (off-plan) or the seller's account via DLD (ready). Opening a UAE bank account only becomes necessary if you take a UAE mortgage or intend to earn rental income locally." },
  { q: "How long does a remote Dubai property purchase take?", a: "Cash purchase: 21–30 days from Form F to title deed. Mortgaged purchase: 45–60 days. Off-plan: signature and Oqood in 7–14 days, handover per developer construction schedule." },
  { q: "Do non-residents pay higher taxes on Dubai property?", a: "No. There is no personal income tax, no capital gains tax and no rental income tax in the UAE, regardless of residency. You pay only a one-off 4% DLD transfer fee at purchase and (if you own a business earning locally) 9% corporate tax on profits above AED 375,000." },
  { q: "Can I get a mortgage in Dubai without living in the UAE?", a: "Yes. Non-resident mortgages are offered by Emirates NBD, Mashreq, HSBC, ADCB, Standard Chartered and Dubai Islamic Bank up to 50–60% LTV, at rates in the 4.75–6.5% range for 2026. Pre-approval takes 3–5 working days for salaried buyers with clean paperwork." },
  { q: "Is Dubai off-plan safe for foreign buyers?", a: "Yes, provided the project sits with a RERA-registered escrow. All buyer funds are ring-fenced in the escrow until construction milestones are certified, and RERA can freeze developer draws for missed deadlines. Point Penta only markets Tier-1 developer projects (Emaar, Nakheel, DAMAC, Sobha, Meraas, Aldar, Select, Ellington)." },
  { q: "What if the deal falls through after I've wired money?", a: "If funds are in RERA escrow (off-plan) or DLD trustee account (ready), they are legally protected. Cancellation refunds trigger within 30–90 days. This is why we never allow direct wires to a seller's personal account — the escrow layer is your protection." },
  { q: "Can I rent out my Dubai property after buying remotely?", a: "Yes. Point Penta operates a full lettings and property-management desk — tenant sourcing, Ejari registration, DEWA transfers, service charge payment, snagging, annual inspections. Most international buyers hand over to us at handover; we handle everything under a management agreement." },
];

export default function BuyFromAbroadPillar() {
  return (
    <PillarPage
      slug="buy-dubai-property-from-abroad"
      category="International buyers"
      title="How to Buy Dubai Property from Abroad — 2026 Complete Guide"
      hero={HERO}
      updated="Feb 2026"
      intro="Roughly 42% of Point Penta transactions close with the buyer never physically in Dubai. This guide walks through the exact remote purchase process — from Power of Attorney to escrow wires, non-resident mortgages, and the Golden Visa that lands 4–8 weeks after your title deed."
      toc={TOC}
      sections={SECTIONS}
      faqs={FAQS}
    />
  );
}
